/** Top-bar title of each route (first match wins, so specific paths come before their parents). */
export const ROUTE_LABELS = [
  { path: '/dashboard', label: 'top-bar.dashboard' },
  { path: '/history/reschedule/:bookingId', label: 'top-bar.reschedule' },
  { path: '/history', label: 'top-bar.my-bookings' },
  { path: '/passes', label: 'top-bar.passes' },
  { path: '/booking', label: 'top-bar.book-court' },
  { path: '/account/setup', label: 'top-bar.account' },
  { path: '/account/:tab', label: 'top-bar.account' },
  { path: '/account', label: 'top-bar.account' },
  { path: '/partners', label: 'top-bar.partners' },
  { path: '/confirm', label: 'top-bar.confirm' },
  { path: '/checkout', label: 'top-bar.checkout' },
  { path: '/notifications', label: 'top-bar.notifications' },
  { path: '/m/booking', label: 'top-bar.new-booking' },
  { path: '/m/all', label: 'top-bar.schedule' },
  { path: '/m/courts/club/:clubId', label: 'top-bar.courts' },
  { path: '/m/courts/new', label: 'top-bar.courts' },
  { path: '/m/courts/:courtId', label: 'top-bar.courts' },
  { path: '/m/courts', label: 'top-bar.courts' },
  { path: '/m/clubs/new', label: 'top-bar.clubs' },
  { path: '/m/clubs/:clubId', label: 'top-bar.clubs' },
  { path: '/m/clubs', label: 'top-bar.clubs' },
  { path: '/m/passes/new', label: 'top-bar.passes' },
  { path: '/m/passes/:passProductId', label: 'top-bar.passes' },
  { path: '/m/passes', label: 'top-bar.passes' },
  { path: '/m/pricing/new', label: 'top-bar.session-prices' },
  { path: '/m/pricing/:priceRuleId', label: 'top-bar.session-prices' },
  { path: '/m/pricing', label: 'top-bar.session-prices' },
  { path: '/m/blocks/new', label: 'top-bar.court-blocks' },
  { path: '/m/blocks/:blockId/edit', label: 'top-bar.court-blocks' },
  { path: '/m/blocks', label: 'top-bar.court-blocks' },
  { path: '/m/payments', label: 'top-bar.payments' },
  { path: '/a/reports', label: 'top-bar.reports' },
  { path: '/a/users/:profileId', label: 'top-bar.users' },
  { path: '/a/users', label: 'top-bar.users' },
  { path: '/a/audit', label: 'top-bar.audit-log' },
  { path: '/help', label: 'top-bar.help' },
] as const

/** What a player does: book, follow their bookings, buy passes, find partners. */
export const USER_NAV_ITEMS = [
  { path: '/dashboard', labelKey: 'side-bar.dashboard', icon: 'home' },
  { path: '/booking', labelKey: 'side-bar.book-court', icon: 'calendar' },
  { path: '/history', labelKey: 'side-bar.my-bookings', icon: 'history' },
  { path: '/passes', labelKey: 'side-bar.passes', icon: 'crown' },
  { path: '/partners', labelKey: 'side-bar.partners', icon: 'users' },
  {
    path: '/account/profile',
    labelKey: 'side-bar.account',
    icon: 'user',
    mobileOnly: true,
  },
] as const

/** Day-to-day club work: the schedule, bookings for customers, court blocks and payments to settle. */
export const OPERATIONS_NAV_ITEMS = [
  { path: '/m/all', labelKey: 'side-bar.schedule', icon: 'grid' },
  { path: '/m/booking', labelKey: 'side-bar.new-booking', icon: 'plus' },
  { path: '/m/blocks', labelKey: 'side-bar.court-blocks', icon: 'block' },
  { path: '/m/payments', labelKey: 'side-bar.payments', icon: 'card' },
] as const

/** What the club offers and on which terms: clubs, courts, prices and passes. */
export const SETUP_NAV_ITEMS = [
  { path: '/m/clubs', labelKey: 'side-bar.clubs', icon: 'flag' },
  { path: '/m/courts', labelKey: 'side-bar.courts', icon: 'indoor' },
  { path: '/m/pricing', labelKey: 'side-bar.session-prices', icon: 'tag' },
  { path: '/m/passes', labelKey: 'side-bar.passes', icon: 'crown' },
] as const

export const ADMIN_NAV_ITEMS = [
  { path: '/a/reports', labelKey: 'side-bar.reports', icon: 'chart' },
  { path: '/a/users', labelKey: 'side-bar.users', icon: 'users' },
  { path: '/a/audit', labelKey: 'side-bar.audit-log', icon: 'lock' },
] as const

/**
 * The account area, grouped by what the customer wants to do. Each tab's page header names its section, so the menu
 * and the page always agree.
 */
export const ACCOUNT_NAV_SECTIONS = [
  {
    labelKey: 'account:account.sections.profile',
    items: [{ path: '/account/profile', labelKey: 'account:account.tabs.profile', icon: 'user' }],
  },
  {
    labelKey: 'account:account.sections.billing',
    items: [
      { path: '/account/payments', labelKey: 'account:account.tabs.payments', icon: 'card' },
      { path: '/account/refunds', labelKey: 'account:account.tabs.refunds', icon: 'cash' },
      { path: '/account/billing', labelKey: 'account:account.tabs.billing-address', icon: 'pin' },
    ],
  },
  {
    labelKey: 'account:account.sections.settings',
    items: [
      { path: '/account/preferences', labelKey: 'account:account.tabs.preferences', icon: 'globe' },
      { path: '/account/notifications', labelKey: 'account:account.tabs.notifications', icon: 'bell' },
    ],
  },
  {
    labelKey: 'account:account.sections.security',
    items: [
      { path: '/account/password', labelKey: 'account:account.tabs.password', icon: 'lock' },
      { path: '/account/terms', labelKey: 'account:account.tabs.terms', icon: 'info' },
      { path: '/account/delete-account', labelKey: 'account:account.tabs.delete-account', icon: 'trash' },
    ],
  },
] as const

/** The help center, last in the menu for every role. */
export const HELP_NAV_ITEMS = [
  { path: '/help', labelKey: 'side-bar.help', icon: 'info' },
] as const

export const BACK_TO_DASHBOARD_NAV_ITEM = {
  path: '/dashboard',
  labelKey: 'side-bar.back-to-dashboard',
  icon: 'chevL',
} as const
